import { Box, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import FormikAutocomplete, { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { ERPJobFormValues } from '../../../../ERPJobValidationSchema';
import { useListDispatchersAutocomplete } from '@/jobs/application/adapters/listDispatchersAutocomplete';
import { useListDispatchLocationsAutocomplete } from '@/jobs/application/adapters/listDispatchLocationsAutocomplete';
import tid from '@/common/application/utils/tid';
import type { NamedEntity } from '@/raptorjob/application/utils/jobDataExtractor';

type DispatcherOption = {
  value: number;
  label: string;
};

interface ERPDispatchInformationSectionProps {
  dispatcher?: NamedEntity | null;
  dispatchLocation?: NamedEntity | null;
  availableDispatchLocations?: Array<{ id: number; name?: string | null; default?: boolean | null }>;
}

export function ERPDispatchInformationSection({
  dispatcher,
  dispatchLocation,
  availableDispatchLocations = []
}: ERPDispatchInformationSectionProps = {}) {
  const { values } = useFormikContext<ERPJobFormValues>();
  const [dispatcherSearchTerm, setDispatcherSearchTerm] = useState('');
  const [dispatchLocationSearchTerm, setDispatchLocationSearchTerm] = useState('');

  const {
    options: dispatcherOptions,
    loading: dispatcherLoading,
    filterOptions: dispatcherFilterOptions
  } = useListDispatchersAutocomplete({ searchTerm: dispatcherSearchTerm });

  const {
    options: dispatchLocationOptions,
    loading: dispatchLocationLoading,
    filterOptions: dispatchLocationFilterOptions
  } = useListDispatchLocationsAutocomplete({ searchTerm: dispatchLocationSearchTerm });

  /**
   * Finds the dispatcher option to display based on the current form value.
   * Checks autocomplete options first, then falls back to jobData.
   */
  const findDispatcherOption = (dispatcherId: number | null | undefined): DispatcherOption | undefined => {
    if (!dispatcherId) return undefined;

    // First: Check autocomplete options (most up-to-date)
    const foundInAutocomplete = dispatcherOptions.find((d) => Number(d.value) === Number(dispatcherId));
    if (foundInAutocomplete) {
      return {
        value: Number(foundInAutocomplete.value),
        label: foundInAutocomplete.label
      };
    }

    // Fallback: Use dispatcher from jobData (edit mode)
    if (dispatcher?.id === dispatcherId && dispatcher.name) {
      return {
        value: dispatcher.id,
        label: dispatcher.name
      };
    }

    return undefined;
  };

  /**
   * Finds the dispatch location option to display based on the current form value.
   * Checks multiple sources in priority order: autocomplete → jobData → form options.
   */
  const findDispatchLocationOption = (locationId: number | null | undefined): OptionType | undefined => {
    if (!locationId) return undefined;

    // Priority 1: Check autocomplete options (most up-to-date)
    const foundInAutocomplete = dispatchLocationOptions.find((l) => Number(l.value) === Number(locationId));
    if (foundInAutocomplete) {
      return {
        value: Number(foundInAutocomplete.value),
        label: foundInAutocomplete.label
      };
    }

    // Priority 2: Use dispatchLocation from jobData (edit mode)
    if (dispatchLocation?.id === locationId && dispatchLocation.name) {
      return {
        value: dispatchLocation.id,
        label: dispatchLocation.name
      };
    }

    // Priority 3: Use form options (handles pre-populated default before autocomplete loads)
    const foundInFormOptions = availableDispatchLocations.find((l) => l.id === locationId);
    if (foundInFormOptions?.name) {
      return {
        value: foundInFormOptions.id,
        label: foundInFormOptions.name
      };
    }

    return undefined;
  };

  const dispatcherInitialOption = useMemo(
    () => findDispatcherOption(values.dispatcherId),
    [values.dispatcherId, dispatcherOptions, dispatcher]
  );

  const dispatchLocationInitialOption = useMemo(
    () => findDispatchLocationOption(values.dispatchLocationId),
    [values.dispatchLocationId, dispatchLocationOptions, dispatchLocation, availableDispatchLocations]
  );

  /**
   * Adds fallback options to ensure selected values appear in dropdowns.
   * This handles cases where values exist but aren't in autocomplete search results yet.
   */
  const addOptionIfMissing = <T extends { value: number | string }>(options: T[], optionToAdd: { value: number; label: string }): T[] => {
    const exists = options.some((opt) => Number(opt.value) === optionToAdd.value);
    if (!exists) {
      return [...options, optionToAdd as unknown as T];
    }
    return options;
  };

  /**
   * Builds dispatcher options list with fallback from jobData.
   * Ensures the selected dispatcher appears in the dropdown even if not in search results.
   */
  const buildDispatcherOptionsWithFallback = (): DispatcherOption[] => {
    const mappedOptions = dispatcherOptions.map((option) => ({
      ...option,
      value: Number(option.value)
    }));

    if (dispatcher?.id && dispatcher.name) {
      return addOptionIfMissing(mappedOptions, {
        value: dispatcher.id,
        label: dispatcher.name
      });
    }

    return mappedOptions;
  };

  /**
   * Builds dispatch location options list with fallbacks.
   * Ensures selected location appears in dropdown from multiple sources:
   * 1. Autocomplete search results
   * 2. JobData (edit mode)
   * 3. Form options (pre-populated default)
   */
  const buildDispatchLocationOptionsWithFallback = (): OptionType[] => {
    let options: OptionType[] = [...dispatchLocationOptions];

    // Add location from jobData if editing
    if (dispatchLocation?.id && dispatchLocation.name) {
      options = addOptionIfMissing(options, {
        value: dispatchLocation.id,
        label: dispatchLocation.name
      });
    }

    // Add pre-populated default from form options (before autocomplete loads)
    if (values.dispatchLocationId && availableDispatchLocations.length > 0) {
      const formOption = availableDispatchLocations.find((l) => l.id === values.dispatchLocationId);
      if (formOption?.name) {
        options = addOptionIfMissing(options, {
          value: formOption.id,
          label: formOption.name
        });
      }
    }

    return options;
  };

  const dispatcherOptionsWithFallback = useMemo(() => buildDispatcherOptionsWithFallback(), [dispatcherOptions, dispatcher]);

  const dispatchLocationOptionsWithFallback = useMemo(
    () => buildDispatchLocationOptionsWithFallback(),
    [dispatchLocationOptions, dispatchLocation, values.dispatchLocationId, availableDispatchLocations]
  );

  return (
    <>
      <Typography variant="h6" component="div" sx={{ fontSize: '1rem', mb: 2, fontWeight: 600 }}>
        Dispatch Information
      </Typography>
      <Box sx={{ display: 'flex', gap: '12px', flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box sx={{ flex: 1 }}>
          <FormikAutocomplete<{ value: number; label: string }, ERPJobFormValues>
            id="dispatcherId"
            required
            label="Dispatcher"
            {...tid('jobs:create:dispatcher-autocomplete')}
            options={dispatcherOptionsWithFallback}
            optionsloading={dispatcherLoading}
            initialOption={dispatcherInitialOption}
            filterOptions={(options, state) => {
              const filtered = dispatcherFilterOptions(options, state);
              return filtered.map((option) => ({
                ...option,
                value: Number(option.value)
              }));
            }}
            onInputChange={(_event: unknown, value: string) => setDispatcherSearchTerm(value)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormikAutocomplete<OptionType, ERPJobFormValues>
            id="dispatchLocationId"
            required
            label="Dispatch/Location Group"
            {...tid('jobs:create:dispatch-location-select')}
            options={dispatchLocationOptionsWithFallback}
            optionsloading={dispatchLocationLoading}
            initialOption={dispatchLocationInitialOption}
            filterOptions={dispatchLocationFilterOptions}
            onInputChange={(_event: unknown, value: string) => setDispatchLocationSearchTerm(value)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
          />
        </Box>
      </Box>
    </>
  );
}
